import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

import {
  Lightning,
  ListDashes,
  X
} from '@phosphor-icons/react';
const LandingHeader = () => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, authStatus } = useAuth();
  const isAuthenticated = authStatus === 'authenticated' && user;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Only show on public/landing pages - NOT on authenticated app pages
  const showOnPaths = ['/', '/features', '/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/about', '/contact', '/pricing', '/terms', '/privacy', '/cookie-policy', '/cookie-settings', '/ai-disclaimer', '/resources', '/consultants', '/agencies', '/partners', '/businesses', '/blog', '/how-we-score', '/help', '/security', '/mcp', '/ai-agents', '/integrations', '/use-cases', '/roadmap', '/releases', '/careers', '/status', '/automations', '/workers', '/marketplace', '/assignment-marker'];
  
  // Explicitly hide on authenticated dashboard/app routes
  const hideOnPaths = ['/dashboard', '/partner-pro', '/admin', '/assessment', '/app', '/inbox', '/apps', '/settings', '/unified-agent-builder'];
  if (hideOnPaths.some(path => location.pathname.startsWith(path))) {
    return null;
  }
  
  // Also hide on /workers or /automations when user is authenticated (inside app shell)
  if (isAuthenticated && (location.pathname.startsWith('/automations') || location.pathname.startsWith('/workers'))) {
    return null;
  }
  
  if (!showOnPaths.some(path => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  })) {
    return null;
  }

  const navItems = [
    { name: 'Features', path: '/features' },
    { name: 'Workers', path: '/marketplace' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'About', path: '/about' },
    { name: 'Resources', path: '/resources' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'bg-[var(--surface-0)]/95 backdrop-blur-sm border-b border-[var(--border-default)]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-transparent rounded-lg flex items-center justify-center overflow-hidden">
              <img 
                src={`${process.env.PUBLIC_URL}/man-and-machine-logo.png`}
                alt="Man & Machine Logo" 
                className="w-10 h-10 object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div>
              <span className="text-sm sm:text-base font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <span className="text-white">MAN</span>
                <span className="text-[#00E5C0] mx-1">&</span>
                <span className="text-white">MACHINE</span>
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className="text-[var(--text-muted)] hover:text-white transition-colors font-medium link-hover"
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated ? (
              <Link
                to="/dashboard"
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0B0F14] font-semibold transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-6 py-2 rounded-full bg-[var(--surface-2)] border border-[var(--border-default)] hover:border-[var(--border-strong)] text-white font-semibold transition-all"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-2 rounded-full bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0B0F14] font-semibold transition-colors"
                >
                  Sign Up Free
                </Link>
              </>
            )}
          </div>

          {/* Mobile ListDashes Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-[var(--text-muted)] hover:text-white transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <ListDashes size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile ListDashes */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[var(--surface-0)]/95 backdrop-blur-sm border-t border-[var(--border-default)]"
          >
            <div className="px-4 py-4 space-y-3">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-4 py-2 rounded-lg text-[var(--text-muted)] hover:text-white hover:bg-[var(--surface-2)] transition-colors"
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-3 border-t border-[var(--border-default)] space-y-2">
                {isAuthenticated ? (
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full px-4 py-2 text-center rounded-full bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0B0F14] font-semibold transition-colors"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block w-full px-4 py-2 text-center rounded-full bg-[var(--surface-2)] border border-[var(--border-default)] hover:border-[var(--border-strong)] text-white font-semibold transition-all"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className="block w-full px-4 py-2 text-center rounded-full bg-[#00E5C0] hover:bg-[#00CCB0] text-[#0B0F14] font-semibold transition-colors"
                    >
                      Sign Up Free
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default LandingHeader;
